import { useState, createContext } from 'react'
import { toast } from 'react-toastify'

import { CONSTANTS } from '@/utils/CONSTANTS'
import { dashboardService } from '@/services/Parametrization/dashboardService'
import { IMetrical } from '@/pages/Home/components/BudgetGraph'
import { IHighIncidenceRepair } from '@/pages/Home/components/HighIncidenceRepair'
import { IKmCostGraphs } from '@/pages/Home/components/KmCostGraphs'
import { IPreparationMediumTimeGraphs } from '@/pages/Home/components/PreparationMediumTimeGraphs'
import { kFormatter } from '../utils'
import { TYPE_OF_VEHICLES } from '@/pages/Inspection/utils/contants'

interface IDashboard {
  children: React.ReactNode
}

type DashboardContextData = {
  GET_SURVAY_BY_COMPANY: (params?: any) => {}
  SURVAY_BY_COMPANY: any
  GET_PREPARATION_BUDGETED_REALIZED: (params?: any) => {}
  PREPARATION_BUDGETED_REALIZED: any
  GET_PREPARATION_AVERAGE_COST_LIGHT_MODEL: (params?: any) => {}
  PREPARATION_AVERAGE_COST_LIGHT_MODEL: any
  GET_PREPARATION_AVERAGE_COST_HEAVY_MODEL: (params?: any) => {}
  PREPARATION_AVERAGE_COST_HEAVY_MODEL: any
  GET_PREPARATION_AVERAGE_COST_LIGHT_KM: (params?: any) => {}
  COMPLEMENTARY_SURVAY_METRICALS: any
  GET_COMPLEMENTARY_SURVAY_METRICALS: (params?: any) => {}
  PREPARATION_AVERAGE_COST_LIGHT_KM: any
  GET_PREPARATION_AVERAGE_COST_HEAVY_KM: (params?: any) => {}
  PREPARATION_AVERAGE_COST_HEAVY_KM: any
  GET_PREPARATION_COST_PER_HOUR_HEAVY: (params?: any) => {}
  PREPARATION_COST_PER_HOUR_HEAVY: any
  GET_PREPARATION_AVERAGE_TIME: (params?: any) => {}
  PREPARATION_AVERAGE_TIME: any
  PREPARATION_FILTERS: any
  UPDATE_PREPARATION_FILTERS: (params?: any) => {}
  GET_TOTAL_BREAKDOWN: (params?: any) => Promise<void>
  TOTAL_BREAKDOWN: any
}

export const DashboardContext = createContext({} as DashboardContextData)

export function DashboardProvider({ children }: IDashboard) {
  const [SURVAY_BY_COMPANY, SET_SURVAY_BY_COMPANY] = useState(null)
  const [PREPARATION_BUDGETED_REALIZED, SET_PREPARATION_BUDGETED_REALIZED] =
    useState([])
  const [
    PREPARATION_AVERAGE_COST_LIGHT_MODEL,
    SET_PREPARATION_AVERAGE_COST_LIGHT_MODEL
  ] = useState([])
  const [COMPLEMENTARY_SURVAY_METRICALS, SET_COMPLEMENTARY_SURVAY_METRICALS] =
    useState([])
  const [
    PREPARATION_AVERAGE_COST_HEAVY_MODEL,
    SET_PREPARATION_AVERAGE_COST_HEAVY_MODEL
  ] = useState([])
  const [
    PREPARATION_AVERAGE_COST_LIGHT_KM,
    SET_PREPARATION_AVERAGE_COST_LIGHT_KM
  ] = useState([])
  const [
    PREPARATION_AVERAGE_COST_HEAVY_KM,
    SET_PREPARATION_AVERAGE_COST_HEAVY_KM
  ] = useState([])
  const [PREPARATION_COST_PER_HOUR_HEAVY, SET_PREPARATION_COST_PER_HOUR_HEAVY] =
    useState([])
  const [PREPARATION_AVERAGE_TIME, SET_PREPARATION_AVERAGE_TIME] = useState([])
  const [PREPARATION_FILTERS, SET_PREPARATION_FILTERS] = useState(null)
  const [TOTAL_BREAKDOWN, SET_TOTAL_BREAKDOWN] = useState({})

  const UPDATE_PREPARATION_FILTERS = async (params?: any) =>
    SET_PREPARATION_FILTERS(params)

  async function GET_SURVAY_BY_COMPANY(params?: any) {
    try {
      const res = await dashboardService.getSurvayTotal(params)
      const survayDetails = res.data.data

      if (!survayDetails) return toast.warning(CONSTANTS.filter.empty)
      if (survayDetails && params) toast.success(CONSTANTS.filter.success)

      const {
        totalVistorias,
        percentualDiferenca,
        totalVistoriasPorEmpresaAvaliadora
      } = survayDetails

      const graphySettings: any = {
        total: totalVistorias,
        percentual: percentualDiferenca,
        companyNames: totalVistoriasPorEmpresaAvaliadora.map(
          (item: { nomeEmpresaAvaliadora: string }) =>
            item.nomeEmpresaAvaliadora
        ),
        morning: totalVistoriasPorEmpresaAvaliadora.map(
          (item: { quantidadeManha: number }) => item.quantidadeManha
        ),
        afternoon: totalVistoriasPorEmpresaAvaliadora.map(
          (item: { quantidadeTarde: number }) => item.quantidadeTarde
        )
      }
      SET_SURVAY_BY_COMPANY(graphySettings)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_COMPLEMENTARY_SURVAY_METRICALS(params?: any) {
    try {
      const res = await dashboardService.getStockQuantity(params)
      const dataTemp = res.data.data
      SET_COMPLEMENTARY_SURVAY_METRICALS(dataTemp)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PREPARATION_BUDGETED_REALIZED(params?: any) {
    try {
      const res = await dashboardService.getBudgetedRealizedValue(params)
      const dataTemp = res.data.data

      if (dataTemp) {
        const result = dataTemp.map(
          (x: any) =>
            ({
              label: x.empresa,
              budgetedAmount: x.valorOrcado,
              realizedValue: x.valorRealizado
            } as IMetrical)
        )

        SET_PREPARATION_BUDGETED_REALIZED(result ?? [])
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PREPARATION_AVERAGE_COST_LIGHT_MODEL(params?: any) {
    try {
      const res = await dashboardService.getAverageCostModel(
        params,
        TYPE_OF_VEHICLES[1]
      )
      const dataTemp = res.data.data

      if (dataTemp) {
        const result = dataTemp.map(
          (x: any, i: number) =>
            ({
              code: x.codigoFipe,
              type: x.modelo,
              value: x.valor,
              top: i === 0
            } as IHighIncidenceRepair)
        )

        SET_PREPARATION_AVERAGE_COST_LIGHT_MODEL(result ?? [])
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PREPARATION_AVERAGE_COST_HEAVY_MODEL(params?: any) {
    try {
      const res = await dashboardService.getAverageCostModel(
        params,
        TYPE_OF_VEHICLES[2]
      )
      const dataTemp = res.data.data

      if (dataTemp) {
        const result = dataTemp.map(
          (x: any, i: number) =>
            ({
              code: x.codigoFipe,
              type: x.modelo,
              value: x.valor,
              top: i === 0
            } as IHighIncidenceRepair)
        )

        SET_PREPARATION_AVERAGE_COST_HEAVY_MODEL(result ?? [])
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PREPARATION_AVERAGE_COST_LIGHT_KM(params?: any) {
    try {
      const res = await dashboardService.getAverageKM(
        params,
        TYPE_OF_VEHICLES[1]
      )
      const dataTemp = res.data.data

      if (dataTemp) {
        const result = dataTemp.map(
          (x: any, i: number) =>
            ({
              label: kFormatter(Number(x.fim), false),
              value: x.montante
            } as IKmCostGraphs)
        )

        SET_PREPARATION_AVERAGE_COST_LIGHT_KM(result ?? [])
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PREPARATION_AVERAGE_COST_HEAVY_KM(params?: any) {
    try {
      const res = await dashboardService.getAverageKM(
        params,
        TYPE_OF_VEHICLES[2]
      )
      const dataTemp = res.data.data

      if (dataTemp) {
        const result = dataTemp.map(
          (x: any, i: number) =>
            ({
              label: kFormatter(Number(x.fim), false),
              value: x.montante
            } as IKmCostGraphs)
        )

        SET_PREPARATION_AVERAGE_COST_HEAVY_KM(result ?? [])
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PREPARATION_COST_PER_HOUR_HEAVY(params?: any) {
    try {
      const res = await dashboardService.getCostPerHour(params)
      const dataTemp = res.data.data

      if (dataTemp) {
        const result = dataTemp.map(
          (x: any, i: number) =>
            ({
              label: kFormatter(Number(x.fim), false),
              value: x.hora
            } as IKmCostGraphs)
        )

        SET_PREPARATION_COST_PER_HOUR_HEAVY(result ?? [])
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PREPARATION_AVERAGE_TIME(params?: any) {
    try {
      const res = await dashboardService.getAveragePerDay(params)
      const dataTemp = res.data.data

      if (dataTemp) {
        const result = dataTemp.map(
          (x: any) =>
            ({
              label: x.categoria,
              value: x.dias
            } as IPreparationMediumTimeGraphs)
        )

        SET_PREPARATION_AVERAGE_TIME(result ?? [])
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_TOTAL_BREAKDOWN(params?: any) {
    try {
      const res = await dashboardService.getTotalBreakdownInspection(params)
      const dataTemp = res.data.data
      SET_TOTAL_BREAKDOWN(dataTemp)
      return dataTemp
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  return (
    <DashboardContext.Provider
      value={{
        GET_SURVAY_BY_COMPANY,
        SURVAY_BY_COMPANY,
        GET_PREPARATION_BUDGETED_REALIZED,
        PREPARATION_BUDGETED_REALIZED,
        GET_PREPARATION_AVERAGE_COST_LIGHT_MODEL,
        PREPARATION_AVERAGE_COST_LIGHT_MODEL,
        GET_PREPARATION_AVERAGE_COST_HEAVY_MODEL,
        PREPARATION_AVERAGE_COST_HEAVY_MODEL,
        GET_PREPARATION_AVERAGE_COST_LIGHT_KM,
        PREPARATION_AVERAGE_COST_LIGHT_KM,
        GET_PREPARATION_AVERAGE_COST_HEAVY_KM,
        PREPARATION_AVERAGE_COST_HEAVY_KM,
        GET_PREPARATION_COST_PER_HOUR_HEAVY,
        PREPARATION_COST_PER_HOUR_HEAVY,
        GET_PREPARATION_AVERAGE_TIME,
        PREPARATION_AVERAGE_TIME,
        PREPARATION_FILTERS,
        COMPLEMENTARY_SURVAY_METRICALS,
        GET_COMPLEMENTARY_SURVAY_METRICALS,
        UPDATE_PREPARATION_FILTERS,
        GET_TOTAL_BREAKDOWN,
        TOTAL_BREAKDOWN
      }}
    >
      {children}
    </DashboardContext.Provider>
  )
}
